/**
 * Интерфейс СУПЕРАДМИНА (роут /admin) — пять разделов:
 *   • Пользователи   — сводка по всем ER-аккаунтам (сессии, задачи, админ-бейдж).
 *   • Все задачи     — задачи ВСЕХ пользователей (без owns-фильтра) + фильтры.
 *   • Аудит действий — журнал действий, новейшие первыми + фильтры.
 *   • Статус системы — состояние egress-маршрутов и ГЛОБАЛЬНОЙ защиты от бана mob.
 *   • ЕДГ-лотерея    — учётка mob-api (регион/логин/пароль) + «Проверить подключение»;
 *                      единственный раздел, который ПИШЕТ, остальные только читают.
 *
 * Доступ к разделу закрыт вкладкой «Админ» (видна только при whoami.is_admin) и на
 * бэкенде эндпоинты за require_admin (403 не-админам). Пишет только раздел «ЕДГ-лотерея»
 * (учётка mob-api) — все остальные запросы GET /api/admin/*.
 */

import * as React from "react";
import { Activity, ListChecks, ScrollText, Shield, Ticket, Users } from "lucide-react";

import type { AdminJob, JobInfo } from "@/api/client";
import {
  useAdminAudit,
  useAdminJobs,
  useAdminLotterySettings,
  useAdminSystem,
  useAdminUsers,
  useLotteryStatus,
  useSaveLotterySettings,
  useTestLotterySettings,
} from "@/api/hooks";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill } from "@/components/ui/Pill";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { joinDot } from "@/lib/lottery";
import { type Tone } from "@/lib/tones";
import { cn } from "@/lib/utils";

/* ───────────────────────────── shared helpers ───────────────────────────── */

/** Format an ISO-8601 string as дд.мм.гггг чч:мм (local), or "—" when empty/invalid. */
function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
}

/** ru-RU grouped number. */
function nf(n: number): string {
  return (n ?? 0).toLocaleString("ru-RU");
}

const KIND_LABELS: Record<string, string> = {
  touch_fill: "Наполнение касания",
  bulk_delete: "Массовое удаление",
  readiness_fill: "Готовность из файла",
  bulk_edit_file: "Обновление из файла",
  uik_branch_fill: "УИК → отделение из файла",
  lottery_turnout: "ЕДГ-лотерея: явка",
  export: "Экспорт",
};

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** Map a job status to a pill label + semantic tone (как в разделе «Задачи»). */
const STATUS_META: Record<JobInfo["status"], { label: string; tone: Tone }> = {
  done: { label: "Успех", tone: "green" },
  running: { label: "Идёт", tone: "blue" },
  pending: { label: "В очереди", tone: "blue" },
  scheduled: { label: "Запланировано", tone: "gray" },
  failed: { label: "Ошибка", tone: "red" },
  cancelled: { label: "Отменена", tone: "amber" },
};

function StatusPill({ status }: { status: JobInfo["status"] }) {
  const meta = STATUS_META[status] ?? { label: status, tone: "gray" as Tone };
  return (
    <Pill tone={meta.tone} withDot>
      {meta.label}
    </Pill>
  );
}

/** Neutral card used for loading / error / empty states inside a section. */
function StateCard({ tone, children }: { tone?: "muted" | "error"; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "rounded-xl border bg-card p-6 text-center text-sm",
        tone === "error" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {children}
    </div>
  );
}

/** Wrap the common isPending / isError / empty flow around a table body. */
function QueryGate({
  isPending,
  isError,
  error,
  isEmpty,
  emptyLabel,
  loadingLabel,
  children,
}: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyLabel: string;
  loadingLabel: string;
  children: React.ReactNode;
}) {
  if (isPending) {
    return (
      <StateCard>
        <span className="inline-flex items-center gap-2">
          <Spinner /> {loadingLabel}
        </span>
      </StateCard>
    );
  }
  if (isError) {
    return (
      <StateCard tone="error">
        Ошибка загрузки{error instanceof Error ? `: ${error.message}` : "."}
      </StateCard>
    );
  }
  if (isEmpty) return <StateCard>{emptyLabel}</StateCard>;
  return <>{children}</>;
}

/** Table shell — horizontal scroll + soft dividers, consistent across sections. */
function DataTable({
  head,
  children,
}: {
  head: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {head}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-soft)]">{children}</tbody>
      </table>
    </div>
  );
}

const TH = "px-4 py-2 font-medium";
const TD = "px-4 py-2.5 align-middle";

/** Sentinel for "все пользователи" in a <Select> (Radix forbids empty-string values). */
const ALL = "__all__";

/* ───────────────────────────── Пользователи ───────────────────────────── */

function UsersSection() {
  const q = useAdminUsers();
  const rows = q.data ?? [];

  return (
    <QueryGate
      isPending={q.isPending}
      isError={q.isError}
      error={q.error}
      isEmpty={rows.length === 0}
      loadingLabel="Загрузка пользователей…"
      emptyLabel="Пользователей пока нет."
    >
      <DataTable
        head={
          <>
            <th className={TH}>Логин</th>
            <th className={TH}>Имя</th>
            <th className={TH}>Регион</th>
            <th className={TH}>Последняя активность</th>
            <th className={cn(TH, "text-right")}>Сессий (актив/всего)</th>
            <th className={cn(TH, "text-right")}>Задач (всего/идёт)</th>
            <th className={TH}>Роль</th>
          </>
        }
      >
        {rows.map((u) => (
          <tr key={u.er_login} className="hover:bg-muted/40">
            <td className={cn(TD, "font-mono text-xs text-foreground")}>{u.er_login}</td>
            <td className={TD}>{u.user}</td>
            <td className={cn(TD, "text-muted-foreground")}>{u.region || "—"}</td>
            <td className={cn(TD, "font-mono text-xs tabular-nums text-muted-foreground")}>
              {formatDateTime(u.last_seen_at)}
            </td>
            <td className={cn(TD, "text-right font-mono tabular-nums")}>
              <span className={u.sessions_active > 0 ? "text-foreground" : "text-muted-foreground"}>
                {nf(u.sessions_active)}
              </span>
              <span className="text-muted-foreground"> / {nf(u.sessions_total)}</span>
            </td>
            <td className={cn(TD, "text-right font-mono tabular-nums")}>
              <span className="text-foreground">{nf(u.jobs_total)}</span>
              {u.jobs_running > 0 && (
                <span className="text-[color:var(--accent-hex)]"> · {nf(u.jobs_running)} идёт</span>
              )}
            </td>
            <td className={TD}>
              {u.is_admin ? <Pill tone="violet">Админ</Pill> : <span className="text-muted-foreground">—</span>}
            </td>
          </tr>
        ))}
      </DataTable>
    </QueryGate>
  );
}

/* ───────────────────────────── Все задачи ───────────────────────────── */

const JOB_STATUS_OPTIONS: { value: JobInfo["status"]; label: string }[] = [
  { value: "running", label: "Идёт" },
  { value: "pending", label: "В очереди" },
  { value: "scheduled", label: "Запланировано" },
  { value: "done", label: "Успех" },
  { value: "failed", label: "Ошибка" },
  { value: "cancelled", label: "Отменена" },
];

/** Progress as "{progress} / {total} (NN%)" or a preparing hint when total is unknown. */
function progressText(job: Pick<AdminJob, "total" | "progress">): string {
  const total = job.total || 0;
  const progress = job.progress || 0;
  if (total <= 0) return "—";
  const pct = Math.min(100, Math.round((progress / total) * 100));
  return `${nf(progress)} / ${nf(total)} (${pct}%)`;
}

function JobsSection() {
  const usersQ = useAdminUsers();
  const [user, setUser] = React.useState<string>(ALL);
  const [status, setStatus] = React.useState<string>(ALL);

  const params = {
    er_login: user === ALL ? undefined : user,
    status: status === ALL ? undefined : (status as JobInfo["status"]),
    limit: 300,
  };
  const q = useAdminJobs(params);
  const rows = q.data ?? [];

  const users = usersQ.data ?? [];

  return (
    <div className="space-y-3">
      {/* Фильтры */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={user} onValueChange={setUser}>
          <SelectTrigger className="h-8 w-[240px]" data-testid="admin-jobs-user">
            <SelectValue placeholder="Пользователь" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все пользователи</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.er_login} value={u.er_login}>
                {u.er_login}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-[200px]" data-testid="admin-jobs-status">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все статусы</SelectItem>
            {JOB_STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {q.isFetching && <Spinner className="text-muted-foreground" />}
      </div>

      <QueryGate
        isPending={q.isPending}
        isError={q.isError}
        error={q.error}
        isEmpty={rows.length === 0}
        loadingLabel="Загрузка задач…"
        emptyLabel="Задач по этому фильтру нет."
      >
        <DataTable
          head={
            <>
              <th className={cn(TH, "text-right")}>ID</th>
              <th className={TH}>Тип</th>
              <th className={TH}>Статус</th>
              <th className={TH}>Пользователь</th>
              <th className={TH}>Описание</th>
              <th className={cn(TH, "text-right")}>Прогресс</th>
              <th className={TH}>Создана</th>
              <th className={TH}>Запуск</th>
            </>
          }
        >
          {rows.map((j) => (
            <tr key={j.id} className="hover:bg-muted/40">
              <td className={cn(TD, "text-right font-mono text-xs tabular-nums text-muted-foreground")}>
                #{j.id}
              </td>
              <td className={TD}>{kindLabel(j.kind)}</td>
              <td className={TD}>
                <StatusPill status={j.status} />
              </td>
              <td className={cn(TD, "font-mono text-xs text-foreground")}>{j.er_login}</td>
              <td className={cn(TD, "max-w-[280px] truncate text-muted-foreground")} title={j.description ?? undefined}>
                {j.description?.trim() || kindLabel(j.kind)}
              </td>
              <td className={cn(TD, "text-right font-mono text-xs tabular-nums text-muted-foreground")}>
                {progressText(j)}
              </td>
              <td className={cn(TD, "font-mono text-xs tabular-nums text-muted-foreground")}>
                {formatDateTime(j.created_at)}
              </td>
              <td className={cn(TD, "font-mono text-xs tabular-nums text-muted-foreground")}>
                {formatDateTime(j.run_at)}
              </td>
            </tr>
          ))}
        </DataTable>
      </QueryGate>
    </div>
  );
}

/* ───────────────────────────── Аудит действий ───────────────────────────── */

function AuditSection() {
  const usersQ = useAdminUsers();
  const [user, setUser] = React.useState<string>(ALL);
  const [action, setAction] = React.useState<string>("");
  const debouncedAction = useDebouncedValue(action, 350);

  const params = {
    er_login: user === ALL ? undefined : user,
    action: debouncedAction.trim() || undefined,
    limit: 300,
  };
  const q = useAdminAudit(params);
  const rows = q.data ?? [];
  const users = usersQ.data ?? [];

  return (
    <div className="space-y-3">
      {/* Фильтры */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={user} onValueChange={setUser}>
          <SelectTrigger className="h-8 w-[240px]" data-testid="admin-audit-user">
            <SelectValue placeholder="Пользователь" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все пользователи</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.er_login} value={u.er_login}>
                {u.er_login}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="Действие (подстрока)…"
          className="h-8 w-[240px]"
          data-testid="admin-audit-action"
        />

        {q.isFetching && <Spinner className="text-muted-foreground" />}
      </div>

      <QueryGate
        isPending={q.isPending}
        isError={q.isError}
        error={q.error}
        isEmpty={rows.length === 0}
        loadingLabel="Загрузка журнала…"
        emptyLabel="Записей по этому фильтру нет."
      >
        <DataTable
          head={
            <>
              <th className={TH}>Время</th>
              <th className={TH}>Пользователь</th>
              <th className={TH}>Действие</th>
              <th className={TH}>Объект</th>
              <th className={TH}>Маршрут</th>
            </>
          }
        >
          {rows.map((a) => (
            <tr key={a.id} className="hover:bg-muted/40">
              <td className={cn(TD, "whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground")}>
                {formatDateTime(a.created_at)}
              </td>
              <td className={cn(TD, "font-mono text-xs text-foreground")}>{a.er_login || "—"}</td>
              <td className={cn(TD, "font-medium text-foreground")}>{a.action}</td>
              <td className={cn(TD, "text-muted-foreground")}>{a.target || "—"}</td>
              <td className={cn(TD, "font-mono text-xs text-muted-foreground")}>{a.route_name || "—"}</td>
            </tr>
          ))}
        </DataTable>
      </QueryGate>
    </div>
  );
}

/* ───────────────────────────── Статус системы ───────────────────────────── */

/** A single metric tile. */
function MetricTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className="mt-1 text-2xl font-semibold tabular-nums"
        style={accent ? { color: "var(--accent-hex, #2563EB)" } : undefined}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function SystemSection() {
  const q = useAdminSystem();

  if (q.isPending) {
    return (
      <StateCard>
        <span className="inline-flex items-center gap-2">
          <Spinner /> Загрузка состояния системы…
        </span>
      </StateCard>
    );
  }
  if (q.isError) {
    return (
      <StateCard tone="error">
        Не удалось загрузить состояние системы
        {q.error instanceof Error ? `: ${q.error.message}` : "."}
      </StateCard>
    );
  }

  const s = q.data;
  if (!s) return <StateCard>Нет данных.</StateCard>;

  return (
    <div className="space-y-4">
      {/* Пояснение про ГЛОБАЛЬНУЮ защиту */}
      <div className="flex gap-3 rounded-xl border bg-[var(--accent-weak)] p-4">
        <Shield className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--accent-weak-text)]" />
        <div className="space-y-1 text-sm">
          <p className="font-semibold text-[color:var(--accent-weak-text)]">
            Защита от бана mob.er.ru — глобальная
          </p>
          <p className="text-[color:var(--accent-weak-text)]/90">
            Ограничение одновременных запросов к mob действует на <b>весь сервер</b>, а не на
            каждого пользователя: в любой момент к mob уходит не более{" "}
            <b>{nf(s.concurrency_max)}</b> запросов одновременно (общий семафор). Плюс на каждый
            egress-маршрут — не более <b>{nf(s.rate_per_sec)}</b> запросов в секунду. После{" "}
            <b>{nf(s.breaker_threshold)}</b> подряд ошибок маршрут уходит в карантин
            (предохранитель), чтобы не словить блокировку.
          </p>
        </div>
      </div>

      {/* Метрики защиты */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <MetricTile
          label="Одновременных к mob (глобально)"
          value={nf(s.concurrency_max)}
          hint="общий лимит на весь сервер"
          accent
        />
        <MetricTile label="Запросов/сек на маршрут" value={nf(s.rate_per_sec)} hint="rate limit" />
        <MetricTile
          label="Порог предохранителя"
          value={nf(s.breaker_threshold)}
          hint="ошибок подряд → карантин"
        />
      </div>

      {/* Счётчики задач */}
      <div className="grid grid-cols-3 gap-3">
        <MetricTile label="Задач выполняется" value={nf(s.jobs_running)} accent />
        <MetricTile label="Задач запланировано" value={nf(s.jobs_scheduled)} />
        <MetricTile label="Задач в очереди" value={nf(s.jobs_pending)} />
      </div>

      {/* Маршруты egress */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold tracking-tight text-foreground">Маршруты egress</h2>
        {s.routes.length === 0 ? (
          <StateCard>Маршруты не настроены.</StateCard>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {s.routes.map((r) => {
              const quarantined = r.quarantined_seconds > 0;
              return (
                <div key={r.name} className="rounded-xl border bg-card p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium text-foreground" title={r.name}>
                      {r.name}
                    </span>
                    <Pill tone={r.healthy ? "green" : "red"} withDot>
                      {r.healthy ? "Здоров" : "Недоступен"}
                    </Pill>
                  </div>
                  <dl className="mt-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">В работе (in-flight)</dt>
                      <dd className="font-mono tabular-nums text-foreground">{nf(r.in_flight)}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">Карантин</dt>
                      <dd className={cn("font-mono tabular-nums", quarantined ? "text-[var(--danger)]" : "text-muted-foreground")}>
                        {quarantined ? `${nf(r.quarantined_seconds)} с` : "—"}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">Страна egress</dt>
                      <dd className="font-mono text-xs uppercase text-foreground">
                        {r.egress_country || "—"}
                      </dd>
                    </div>
                  </dl>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

/* ───────────────────────────── ЕДГ-лотерея ───────────────────────────── */

function LotterySection() {
  const q = useAdminLotterySettings();
  const status = useLotteryStatus();
  const save = useSaveLotterySettings();
  const test = useTestLotterySettings();

  const [region, setRegion] = React.useState<string>("");
  const [login, setLogin] = React.useState("");
  const [password, setPassword] = React.useState("");

  React.useEffect(() => {
    if (q.data) {
      setRegion(String(q.data.region));
      if (q.data.login) setLogin(q.data.login);
    }
  }, [q.data]);

  if (q.isPending) return <StateCard><Spinner className="mx-auto h-5 w-5" /></StateCard>;
  if (q.isError) return <StateCard tone="error">Не удалось загрузить настройки.</StateCard>;
  const s = q.data;
  const regionNum = Number(region);
  const canSave = Number.isInteger(regionNum) && regionNum >= 1 && login.trim() !== "" && password !== "" && !save.isPending;

  // Части подписи собираем массивом: в состоянии «не настроено» первая часть пуста, и
  // конкатенация оставляла висячее « · Передано телефонов…» на первом экране админа.
  const meta = joinDot([
    s.configured ? `Обновлено ${formatDateTime(s.updated_at)}${s.updated_by ? ` · ${s.updated_by}` : ""}` : null,
    status.data
      ? `Передано телефонов: ${nf(status.data.sent_total)}${status.data.last_sent_at ? `, последний раз ${formatDateTime(status.data.last_sent_at)}` : ""}`
      : null,
  ]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    save.mutate({ region: regionNum, login: login.trim(), password }, { onSuccess: () => setPassword("") });
  }

  return (
    <div className="space-y-4" data-testid="admin-lottery">
      <div className="flex gap-3 rounded-xl border bg-[var(--accent-weak)] p-4 text-sm">
        <Shield className="h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p>
            {s.configured ? <>Учётка настроена: <span className="font-semibold">{s.login}</span>, регион {s.region}.</> : "Учётка mob-api не настроена — передача явки недоступна операторам."}
          </p>
          {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
        </div>
      </div>

      <form onSubmit={onSubmit} className="max-w-md space-y-4 rounded-xl border bg-card p-6 shadow-sm" data-testid="admin-lottery-form">
        <div className="space-y-1.5">
          <Label htmlFor="lottery_region">Код региона</Label>
          <Input id="lottery_region" type="number" min={1} value={region} onChange={(e) => setRegion(e.target.value)} disabled={save.isPending} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lottery_login">Логин mob-api</Label>
          <Input id="lottery_login" type="text" autoComplete="off" value={login} onChange={(e) => setLogin(e.target.value)} disabled={save.isPending} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lottery_password">Пароль mob-api</Label>
          <Input id="lottery_password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={save.isPending} placeholder={s.configured ? "введите заново, чтобы изменить" : ""} />
        </div>
        {save.isError && <p className="text-sm text-destructive" role="alert">Не удалось сохранить{save.error instanceof Error ? `: ${save.error.message}` : "."}</p>}
        {save.isSuccess && <p className="text-sm" style={{ color: "var(--success, #11774E)" }}>Сохранено.</p>}
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={!canSave}>{save.isPending ? <Spinner /> : null}Сохранить</Button>
          {/* Проба идёт по СОХРАНЁННОЙ учётке: при несохранённом пароле кнопка заперта —
              иначе админ увидел бы «ok» на старом пароле и решил бы, что новый рабочий. */}
          <Button
            type="button"
            variant="outline"
            disabled={!s.configured || test.isPending || password !== ""}
            title={password !== "" ? "Сначала сохраните: проверяется сохранённая учётка" : undefined}
            onClick={() => test.mutate()}
          >
            {test.isPending ? <Spinner /> : null}Проверить подключение
          </Button>
        </div>
        {test.data && (
          <p className="text-sm" style={{ color: test.data.ok ? "var(--success, #11774E)" : "var(--danger, #A93A3A)" }} role="status">
            {test.data.message}{test.data.status != null ? ` (HTTP ${test.data.status})` : ""}
          </p>
        )}
        {test.isError && <p className="text-sm text-destructive">Проверка не удалась{test.error instanceof Error ? `: ${test.error.message}` : "."}</p>}
      </form>
    </div>
  );
}

/* ───────────────────────────── page shell ───────────────────────────── */

const TABS = [
  { key: "users", label: "Пользователи", Icon: Users },
  { key: "jobs", label: "Все задачи", Icon: ListChecks },
  { key: "audit", label: "Аудит действий", Icon: ScrollText },
  { key: "system", label: "Статус системы", Icon: Activity },
  { key: "lottery", label: "ЕДГ-лотерея", Icon: Ticket },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function AdminPage() {
  const [tab, setTab] = React.useState<TabKey>("users");

  return (
    <div className="flex h-screen flex-col" data-testid="admin-page">
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-[22px] py-[18px]">
        <div className="mx-auto max-w-6xl space-y-4">
          <header className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Администрирование</h1>
            <p className="text-sm text-muted-foreground">
              Обзор всех пользователей, их задач, журнал действий и состояние защиты от бана mob.
              Только просмотр.
            </p>
          </header>

          {/* Табы разделов */}
          <nav className="flex flex-wrap items-center gap-[3px] text-[13px]">
            {TABS.map(({ key, label, Icon }) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-[8px] px-3 py-[6px] transition-colors",
                    active
                      ? "bg-[var(--accent-weak)] font-semibold text-[var(--accent-weak-text)]"
                      : "font-medium text-[var(--icon-muted)] hover:text-foreground",
                  )}
                  data-testid={`admin-tab-${key}`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              );
            })}
          </nav>

          {tab === "users" && <UsersSection />}
          {tab === "jobs" && <JobsSection />}
          {tab === "audit" && <AuditSection />}
          {tab === "system" && <SystemSection />}
          {tab === "lottery" && <LotterySection />}
        </div>
      </main>
    </div>
  );
}
